import * as sdk from '@cirqll/sdk';
import type { FilterType } from '~/types/filter_type';
import { FieldType, type RecurrenceType } from '@cirqll/sdk/enum';

export function unknownToBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'string') {
        return ['ja', 'yes', 'oui', 'si', 'sí', 'hai', 'shi', '是', '1', 'true', 'x'].includes(value.toLowerCase().slice(0, 4));
    }

    return !!value;
}

/**
 *
 * @param {number} price
 * @param {boolean} round round the number to nearest integer
 * @returns {string} price to human form
 */
export function transformPrice(price: string | number, round: boolean = false): string {
    if (typeof price === 'string') {
        // oxlint-disable-next-line no-console
        console.warn('Price is not of number type');
        price = +price;
    }

    if (round) {
        price = Math.round(price);
    }

    return price
        .toFixed(2)
        .replace('.', ',')
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.')
        .replace(',00', '');
}

/**
 * @returns bytes as localized file size, in KB below 1 MB, e.g. "512 kB" or "1,3 MB"
 */
export function formatFileSize(bytes: number, locale: string): string {
    const isMegabytes = bytes >= 1024 * 1024;
    return new Intl.NumberFormat(locale, {
        style: 'unit',
        unit: isMegabytes ? 'megabyte' : 'kilobyte',
        maximumFractionDigits: 1,
    }).format(isMegabytes ? bytes / 1024 / 1024 : bytes / 1024);
}

/**
 * @returns the file name split into its name and extension (including the dot), e.g. ['offer', '.pdf']
 */
export function splitFileName(fileName: string): [name: string, extension: string] {
    const dot = fileName.lastIndexOf('.');
    if (dot <= 0) {
        return [fileName, ''];
    }

    return [fileName.slice(0, dot), fileName.slice(dot)];
}

/**
 * Files are immutable, so this returns a copy with the new name; the extension is kept
 */
export function renameFile(file: File, name: string): File {
    return new File([file], name + splitFileName(file.name)[1], { type: file.type, lastModified: file.lastModified });
}

/**
 *
 * @export
 * @param {string} price
 * @return string to database form
 */
export function toPrice(price: string) {
    return +price.replace(' ', '').replace(/[.]/g, '').replace(/,/g, '.');
}

/**
 *
 * @export
 * @param {sdk.Chance[]} chances
 * @return total price of chance array
 */
export function chanceTotalPrice(chances: sdk.Chance[]) {
    let total = 0;
    for (const chance of chances) {
        total += chance.price;
    }
    return transformPrice(total);
}

/**
 * Calculates amount of months between 2 dates
 *
 * @export
 * @param {Date} startDate
 * @param {Date} endDate
 * @return {number} amount of months
 */
export function diffMonths(startDate: Date, endDate: Date): number {
    let months = (endDate.getFullYear() - startDate.getFullYear()) * 12;
    months -= startDate.getMonth();
    months += endDate.getMonth() + 1;
    return months < 1 ? 1 : months;
}

/**
 * return amount of days in month
 *
 * @export
 * @param {number} month
 * @param {number} year
 * @return {number} amount
 */
export function daysInMonth(month: number, year: number) {
    return new Date(year, month, 0).getDate();
}

/**
 * Returns a words short name
 * Gameshop Twente => GT
 *
 * @export
 * @param {string} name
 * @return {string} short name
 */
export function shortName(name: string): string {
    // If name is falsey
    if (!name) {
        return '??';
    }

    // Replace dots with nothing (B.V. => BV)
    name = name.replace(/[^a-zA-Z0-9\s]/g, '')
        // And then replace double spaces with a single one
        .replace(/ {2,}/g, ' ');
    name = name.trim();

    // Split name into parts
    const parts = name.split(' ');
    // First will be capitalized
    const first = parts.shift() || '??';

    // Last will also be capitalized
    const last = parts.pop() ?? (first[1] || '');
    // Tim ter Schaar, will become TS
    return (first[0] + (last[0] || '')).toUpperCase();
}

/**
 * Formats phone number
 *
 * @export
 * @param {string} number
 * @return {string} formattedNumber
 */
export function phoneNumber(number: string) {
    return number.replace(/\s|-/g, '');
}

/**
 * Convert string to hash (unique number)
 *
 * @export
 * @param {string} string
 * @return {number} unique hash
 */
export function generateHash(string: string): number {
    let hash = 0;
    for (const char of string) {
        hash = (hash << 5) - hash + char.codePointAt(0)!;
        hash &= hash;
    }
    return hash;
}

/**
 * Transform an array of Field Values to sdk.Filter
 *
 * @export
 */
export function useTransformFieldsToFieldsFilter<T extends { field_values?: sdk.FieldValue[] }>() {
    const { data: fields } = useFields();

    function transform(type: FilterType, fieldValues: sdk.FieldValue[]): sdk.Filter<T> {
        const field_map = new Map(fields.value?.map((f) => [f.id, f]) ?? []);

        return sdk.Filter.builder<{ field_values: sdk.Paginate<sdk.FieldValue> }>()
            .each(fieldValues, (filter, field) => {
                const key = `field_values.${field.field_id}` as any;
                const field_type = field_map.get(field.field_id)?.type;
                const is_multi = field_type === FieldType.MULTI_SELECT || field_type === FieldType.LABELS;

                if (!Array.isArray(field.value)) {
                    return type === 'or'
                        ? filter.or(f => f.where(key, 'eq', field.value))
                        : filter.where(key, 'eq', field.value);
                }

                if (!is_multi || type === 'or') {
                    return type === 'or'
                        ? filter.or(f => f.where(key, 'in', field.value))
                        : filter.where(key, 'in', field.value);
                }

                // multiselect/labels with AND: one condition per value
                for (const value of field.value) {
                    filter.where(key, 'in', value);
                }

                return filter;
            });
    }

    return { transform };
}

export function transformTaskToTaskBlueprint(task: sdk.Task, recurrence: RecurrenceType, recurrence_end_date?: Date): sdk.TaskBlueprintPost {
    return {
        ...task,
        recurrence,
        recurrence_end_date,
        users: task.users?.data.map((item) => ({ id: item.id })),
        assignments: task.assignments?.data.map((item) => ({ id: item.id })),
        contracts: task.contracts?.data.map((item) => ({ id: item.id })),
        contacts: task.contacts?.data.map((item) => ({ id: item.id })),
        chances: task.chances?.data.map((item) => ({ id: item.id })),
        field_values: task.field_values?.map((item) => ({ field_id: item.field_id, value: item.value })),
        subtask_blueprints: task.subtasks?.data.map((item) => ({ user_id: item.user_id, name: item.name, position: item.position })),
    };
}
